import { Box, Button, Flex, HStack, IconButton } from '@chakra-ui/react';
import { EditorContent, JSONContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { isDefined } from '@duke-hq/lib';

import { motion } from 'framer-motion';
import { RotateCw, X } from 'lucide-react';
import { useContext, useEffect, useState, useRef } from 'react';
import { getConnectedEdges, getOutgoers, Node, useReactFlow } from 'reactflow';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { CenteredSpinner } from '@duke-hq/app-admin-web/components/Loading';
import { useGetPersonaById } from '../tiptap/simulation/dBlock/useGetPersonaById';
import { withGrouping } from '../tiptap/simulation/dBlock/conversationGrouping';
import { ConversationPlaybackControl } from '../tiptap/simulation/dBlock/ConversationPlaybackControl';
import { DEFAULT_CONVERSATION_PLAYBACK_SPEED } from '@duke-hq/shared-constants/conversationPlaybackSpeeds';
import {
  PlaybackSpeed,
  getBlockAnimationDelayMS,
} from '../tiptap/simulation/dBlock/conversationPlayback';
import { dukeTTEditorState, TTEditorModeEnum } from '../dukeTTEditorState';
import { flowEditorState } from '../flowEditorState';
import { FlowEditorStateContext } from '../FlowEditorStateContext';
import { NodeData } from '../nodes/flowEditorBlockNodeTypes';
import { FileExtension } from '../tiptap/FileTTComponent';
import { ResizableMediaExtension } from '../tiptap/resizableMedia/ResizableMediaExtension';
import { StudentDBlockExtension } from '../tiptap/simulation/dBlock/StudentDBlockExtension';
import { ReplyComponent } from '../tiptap/simulation/replyComponent';
import { StudentQuizOptionExtension } from '../tiptap/simulation/StudentQuizOptionExtension';
import { Link, dukeQuestionBlockType } from '../../tiptap';
import { ConversationTypingIndicator } from '@duke-hq/ui/components/Conversation/ConversationTypingIndicator';
import { ConversationOptionButton } from '@duke-hq/ui/components/Conversation/ConversationOptionButton';
import { ConversationReplyInput } from '@duke-hq/ui/components/Conversation/ConversationReplyInput';
import { ConversationEndMessage } from '@duke-hq/ui/components/Conversation/ConversationEndMessage';
import { useConversationAutoScroll } from '@duke-hq/ui/hooks/useConversationAutoScroll';
import delay from 'delay';

type Option = {
  id: string;
  title: string;
};

// The language controls forwarded into the playback toolbar, so the canvas panel
// shows the same reading-language picker as the modal. Optional: the picker only
// appears when the panel is given translation context.
type PreviewLanguageControls = {
  value: string;
  onChange: (language: string) => void;
  readyByLanguage?: ReadonlyMap<string, boolean>;
};

export function SimulationTiptap({ language }: { language?: PreviewLanguageControls } = {}) {
  const { getNodes, getEdges } = useReactFlow();
  const { simulationButtonProps, hiddenSimulation, setActiveNode } =
    useContext(FlowEditorStateContext);

  // TODO refactor all state in here into a valtio store so that it can be easily unit tested
  const [currentOptionNode, setCurrentOptionNode] = useState<Node | null>(null);
  const [replyNode, setReplyNode] = useState<Node | null>(null);
  const [reply, setReply] = useState('');
  const [ended, setEnded] = useState(false);

  const videoPlayingRef = useRef<boolean>(false);
  // The preview runs once; the replay control replays it deliberately.
  const startedRef = useRef(false);
  // Bumped on every (re)start so a superseded traversal abandons itself instead
  // of interleaving its messages with the new run.
  const traversalIdRef = useRef(0);

  const [simulationSpeed, setSimulationSpeed] = useState<PlaybackSpeed>(
    DEFAULT_CONVERSATION_PLAYBACK_SPEED,
  );
  const [showEllipsis, setShowEllipsis] = useState(false);

  const simulationSpeedRef = useRef(simulationSpeed);
  simulationSpeedRef.current = simulationSpeed;

  const { getPersonaById, personasReady } = useGetPersonaById();
  const { containerRef, onSelectionUpdate } = useConversationAutoScroll<HTMLDivElement>();

  const editor = useEditor({
    onSelectionUpdate,
    extensions: [
      StudentQuizOptionExtension,
      ReplyComponent,
      FileExtension,
      ResizableMediaExtension.configure({
        onVideoComplete: () => {
          videoPlayingRef.current = false;
        },
        onPlaying: () => {
          videoPlayingRef.current = true;
        },
        onReady: () => {
          videoPlayingRef.current = true;
        },
        autoPlay: true,
      }),
      Link.configure({
        autolink: true,
        linkOnPaste: true,
        openOnClick: false,
      }),
      StudentDBlockExtension.configure({ getPersonaById }),
      StarterKit.configure({}),
      Underline,
    ],
    editable: false,
  });

  useEffect(() => {
    return () => {
      flowEditorState.simulationActive = false;
    };
  }, []);

  async function waitForVideoStatus() {
    return new Promise((resolve) => {
      const timerId = setInterval(checkState, 1000);

      function checkState() {
        if (!videoPlayingRef.current) {
          clearInterval(timerId);
          resolve(videoPlayingRef.current);
        }
      }
    });
  }

  // Stamp a single top-level node (a dBlock, replyComponent or quiz block) with
  // the simulation attrs. Returned nodes become direct children of the editor
  // doc, matching the student runtime, so consecutive same-speaker dBlocks are
  // true siblings and avatar/name grouping works.
  const buildSimulationBlock = (
    block: NonNullable<JSONContent['content']>[number],
    index: number,
    animate: boolean,
  ): JSONContent => ({
    ...block,
    attrs: {
      ...block.attrs,
      simulation: true,
      animation: animate,
      animationIndex: index,
    },
  });

  // A node's content is either a `doc` wrapping several blocks (a flow node's
  // tiptap) or a single block node (a reply echo / quiz block). Either way,
  // return the flat list of top-level blocks to append.
  const topLevelBlocks = (newData: JSONContent): NonNullable<JSONContent['content']> =>
    newData.type === 'doc' ? (newData.content ?? []) : [newData];

  const appendBlock = (block: JSONContent) => {
    const root = editor?.getJSON();
    const blocks =
      !root || root.content === undefined || editor?.isEmpty ? [block] : [...root.content, block];
    editor?.commands.setContent({ type: 'doc', content: withGrouping(blocks) });
  };

  const addContentToEditor = async (newData: JSONContent, runId: number) => {
    if (!newData) {
      console.warn('no newData');
      return;
    }
    const blocks = topLevelBlocks(newData);

    // In "skip animation" mode, append every block at once with no fade or delay,
    // so the conversation completes instantly while keeping whatever is already
    // on screen.
    if (simulationSpeedRef.current === 'instant') {
      setShowEllipsis(false);
      blocks.forEach((block, index) => appendBlock(buildSimulationBlock(block, index, false)));
      return;
    }

    let index = 0;
    for (const block of blocks) {
      // A newer run started (e.g. replay); abandon this one before writing, so
      // its blocks don't interleave with the fresh run's on the cleared editor.
      if (traversalIdRef.current !== runId) {
        return;
      }

      setShowEllipsis(true);
      appendBlock(buildSimulationBlock(block, index, true));

      await waitForVideoStatus();
      await delay(getBlockAnimationDelayMS(block, simulationSpeedRef.current));

      index++;
    }
  };

  const addReply = async () => {
    setReplyNode(null);

    const runId = traversalIdRef.current;
    const newReply: JSONContent = {
      type: 'replyComponent',
      content: [{ type: 'text', text: reply }],
    };
    await addContentToEditor(newReply, runId);

    const nextNode = getOutgoers(replyNode!, getNodes(), getEdges());

    if (nextNode.length > 0) {
      if (nextNode[0] === undefined) {
        throw new Error('nextNode is undefined');
      }
      performTraversal(nextNode[0], runId);
    } else {
      console.warn(`Node: ${replyNode?.id} does not have outgoer`);
    }
  };

  const endInstantTraversal = () => {
    setEnded(true);
    setShowEllipsis(false);
  };

  const advanceFromOption = async (optionNode: Node, option: Option, runId: number) => {
    const nodes = getNodes();
    const edges = getConnectedEdges([optionNode], getEdges());

    const edgeFromOption = edges.find(
      (edge) => edge.source === optionNode.id && edge.sourceHandle === option.id,
    );
    if (!edgeFromOption) {
      // A dead-end fork must not silently stall a "skip animation" run.
      if (simulationSpeedRef.current === 'instant') {
        endInstantTraversal();
      }
      return;
    }

    const nextNode = nodes.find((node) => node.id === edgeFromOption.target);
    if (!nextNode) {
      console.warn('could not find next node from option');
      if (simulationSpeedRef.current === 'instant') {
        endInstantTraversal();
      }
      return;
    }

    setCurrentOptionNode(null);

    // Quiz options aren't echoed back as a learner reply bubble; block options are.
    // Await the echoed reply before traversing on, otherwise both append to the
    // editor concurrently and the messages land out of order.
    if (!optionNode.data.quizId) {
      const newReply: JSONContent = {
        type: 'replyComponent',
        content: [{ type: 'text', text: option.title }],
      };
      await addContentToEditor(newReply, runId);
    }

    performTraversal(nextNode, runId);
  };

  const getNodeFromOption = (option: Option) => {
    if (currentOptionNode) {
      advanceFromOption(currentOptionNode, option, traversalIdRef.current);
    }
  };

  // For "skip animation": pick the first option whose edge actually leads to a
  // node, so a fork whose first option is unconnected doesn't stall the run.
  const advanceFromFirstConnectedOption = (optionNode: Node, options: Option[], runId: number) => {
    const edges = getConnectedEdges([optionNode], getEdges());
    const connected = options.find((option) =>
      edges.some((edge) => edge.source === optionNode.id && edge.sourceHandle === option.id),
    );
    if (connected) {
      advanceFromOption(optionNode, connected, runId);
    } else {
      endInstantTraversal();
    }
  };

  const nodes = getNodes();
  const quizIds = nodes.map((node) => node.data.quizId).filter(isDefined);

  const quizQuery = trpcAdmin.questions.byIds.useQuery(quizIds, {
    staleTime: 10 * 1000,
  });

  const performTraversal = async (node: Node, runId: number) => {
    // A newer run started; abandon this one so its messages don't interleave.
    if (traversalIdRef.current !== runId) {
      return;
    }
    if (!node || node.type === 'end') {
      setEnded(true);
      setShowEllipsis(false);

      return;
    }
    const nodeData: NodeData = node.data;
    // const totalNodes = nodeData.tiptap?.content?.length
    // In "skip animation" mode, don't highlight/pan each node on the canvas -
    // the preview should appear at once without visibly walking the graph.
    if (simulationSpeedRef.current !== 'instant') {
      setActiveNode(node);
    }
    switch (node.type) {
      case 'end':
        break;
      case 'start':
      default:
        if (nodeData.tiptap) {
          await addContentToEditor(nodeData.tiptap, runId);
        }

        // A newer run started while the messages were animating; bail before
        // pausing on options/reply/quiz, otherwise this stale run leaks its
        // controls into the fresh run.
        if (traversalIdRef.current !== runId) {
          return;
        }

        if (nodeData.options && !nodeData.quizId) {
          // In "show all at once" mode, forks are auto-advanced by taking the
          // first option that leads somewhere, so the whole conversation renders
          // without waiting for input.
          if (simulationSpeedRef.current === 'instant') {
            advanceFromFirstConnectedOption(node, nodeData.options, runId);
            return;
          }

          setCurrentOptionNode(node);

          return;
        } else if (nodeData.reply) {
          if (simulationSpeedRef.current === 'instant') {
            const outgoer = getOutgoers(node, getNodes(), getEdges())[0];
            if (outgoer) {
              performTraversal(outgoer, runId);
            } else {
              endInstantTraversal();
            }
            return;
          }
          setReplyNode(node);
          return;
        } else if (nodeData.quizId) {
          const question = quizQuery.data?.find((q) => q.id === nodeData.quizId);
          if (question) {
            addContentToEditor(
              {
                type: dukeQuestionBlockType,
                content: question.content,
                attrs: {
                  questionId: question.id,
                },
              },
              runId,
            );

            // A quiz is an option fork keyed by "<quizId>-correct/-incorrect";
            // in "skip animation" mode take the first connected answer and keep
            // going, the same as any other fork.
            if (simulationSpeedRef.current === 'instant') {
              if (nodeData.options) {
                advanceFromFirstConnectedOption(node, nodeData.options, runId);
              } else {
                endInstantTraversal();
              }
              return;
            }

            setCurrentOptionNode(node);
          }
          return;
        }
    }

    const outgoers = getOutgoers(node, getNodes(), getEdges());

    if (simulationSpeedRef.current !== 'instant') {
      await delay(500);
    }

    // A newer run started while we were waiting; abandon this one.
    if (traversalIdRef.current !== runId) {
      return;
    }

    // A node with nowhere left to go is the end of the conversation, so finish
    // gracefully (showing the end message) rather than throwing.
    if (outgoers[0] === undefined) {
      setEnded(true);
      setShowEllipsis(false);
      return;
    }
    performTraversal(outgoers[0], runId);
  };

  const traversalFromStart = () => {
    flowEditorState.simulationActive = true;
    dukeTTEditorState.mode = TTEditorModeEnum.PREVIEW;

    // Replaying always animates from the start. "Skip animation" is a one-shot,
    // so leave instant mode here, otherwise a replay would just re-render the
    // finished conversation instantly and look like nothing happened.
    if (simulationSpeedRef.current === 'instant') {
      simulationSpeedRef.current = DEFAULT_CONVERSATION_PLAYBACK_SPEED;
      setSimulationSpeed(DEFAULT_CONVERSATION_PLAYBACK_SPEED);
    }

    // Clear first so a replay never appends onto an existing run (which would
    // duplicate the messages), and bump the run id so any in-flight traversal
    // abandons itself instead of interleaving with this one.
    const runId = ++traversalIdRef.current;
    videoPlayingRef.current = false;
    editor?.commands.clearContent();
    setEnded(false);
    setReplyNode(null);
    setCurrentOptionNode(null);

    const start = getNodes().find((node) => node.type === 'start') as Node;
    performTraversal(start, runId);
  };

  useEffect(() => {
    // Start once personas are ready, otherwise the first block renders the Duke
    // fallback and won't re-render once the real persona resolves.
    if (!hiddenSimulation && personasReady && !startedRef.current) {
      startedRef.current = true;
      traversalFromStart();
    } else if (hiddenSimulation) {
      // Hand the editor back to canvas editing.
      dukeTTEditorState.mode = TTEditorModeEnum.EDIT;
    }
  }, [hiddenSimulation, personasReady]);

  // Switching to "skip animation" while the preview is paused at a fork or a
  // reply prompt resumes the run, so the rest of the conversation renders
  // instead of waiting on input that "skip" is meant to bypass.
  const handleSpeedChange = (next: PlaybackSpeed) => {
    setSimulationSpeed(next);
    // Drive the rest of the resume off the new value now, before the re-render
    // that syncs the ref, so the traversal below runs in instant mode.
    simulationSpeedRef.current = next;
    if (next !== 'instant') {
      return;
    }
    const runId = traversalIdRef.current;
    if (currentOptionNode) {
      setCurrentOptionNode(null);
      advanceFromFirstConnectedOption(currentOptionNode, currentOptionNode.data.options, runId);
    } else if (replyNode) {
      const outgoer = getOutgoers(replyNode, getNodes(), getEdges())[0];
      setReplyNode(null);
      if (outgoer) {
        performTraversal(outgoer, runId);
      } else {
        endInstantTraversal();
      }
    }
  };

  if (!editor) {
    return <CenteredSpinner />;
  }

  return (
    <Flex as="section" minH="100%" borderLeftWidth="1px">
      <Flex flex="1" bg="bg.subtle" flexDir="column">
        {/* No title; the panel is self-evidently the preview (it's full of the
            running conversation, and the Preview button opened it). The close
            control just sits top-right, on the same background as the panel. */}
        <HStack px="2" py="2" justifyContent="flex-end" alignItems="center">
          <IconButton
            {...simulationButtonProps()}
            variant="ghost"
            size="sm"
            aria-label="close preview"
          >
            <X size="16px" color="currentColor" />
          </IconButton>
        </HStack>
        <Flex
          ref={containerRef}
          flex="1 0 0"
          flexDir="column"
          w="full"
          overflowY="auto"
          overflowX={'hidden'}
          gap="8px"
          p="16px"
          alignItems="flex-start"
          alignSelf="stretch"
          bg="bg.muted"
          // The ReactFlow canvas sets `white-space: nowrap` on its nodes, which
          // the conversation content inherits and would otherwise render on a
          // single overflowing line. Reset it once here so every preview child
          // wraps like it does in the student runtime.
          whiteSpace="normal"
          // `containerType` makes this a query container so the preview's responsive
          // content sizes against the panel width (~400px), not the admin viewport.
          // Otherwise the preview shows the wide-viewport layout in a narrow panel.
          sx={{
            containerType: 'inline-size',
            // Spacing between message bubbles, matching the student runtime.
            '& .tiptap > * + *': { marginTop: 'var(--chakra-space-1)' },
          }}
        >
          {/* mx="auto" centres the intrinsic-width control group and lets it
              contribute its width to the content-sized panel, so a longer language
              name widens the panel rather than crowding the edges. */}
          <Box mx="auto">
            <ConversationPlaybackControl
              speed={simulationSpeed}
              onChange={handleSpeedChange}
              onRestart={traversalFromStart}
              language={language}
            />
          </Box>
          {!editor.isEmpty && <EditorContent style={{ width: '100%' }} editor={editor} />}
          {currentOptionNode?.data.options.map((option: Option, i: number) => (
            <motion.div
              key={option.id}
              style={{ alignSelf: 'flex-end', maxWidth: '70%' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{
                duration: 0.8,
                delay: i / 2,
                ease: [0, 0.71, 0.2, 1.01],
              }}
              hidden={currentOptionNode.data.quizId}
            >
              <ConversationOptionButton onClick={() => getNodeFromOption(option)}>
                {option.title}
              </ConversationOptionButton>
            </motion.div>
          ))}

          <ConversationTypingIndicator
            visible={showEllipsis}
            speed={simulationSpeed === 'instant' ? undefined : simulationSpeed}
          />

          {ended ? (
            <ConversationEndMessage
              action={
                <Button
                  sx={{ width: '100%', '@container (min-width: 48em)': { width: 'fit-content' } }}
                  leftIcon={<RotateCw />}
                  onClick={traversalFromStart}
                  mt="2"
                  variant="outline"
                >
                  Replay conversation
                </Button>
              }
            />
          ) : null}
        </Flex>
        {replyNode ? (
          <ConversationReplyInput value={reply} onChange={setReply} onSubmit={() => addReply()} />
        ) : null}
      </Flex>
    </Flex>
  );
}
